import { CarFront, FileText, LayoutDashboard, LogOut, Plus, RefreshCw, Settings2, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import BrandLogo from "../components/BrandLogo";
import { AdminVehicleEditor } from "../components/AdminVehicleEditor";
import AdminSettingsPanel from "../components/AdminSettingsPanel";
import AdminTextEditor from "../components/AdminTextEditor";
import { NoIndex } from "../components/SeoMetadata";
import api, { formatApiError } from "../lib/api";

export default function AdminDashboardPage() {
  const [admin, setAdmin] = useState(null);
  const [settings, setSettings] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [tab, setTab] = useState("overview");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncingFinn, setSyncingFinn] = useState(false);
  const [notice, setNotice] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [user, currentSettings, currentVehicles, currentInquiries] = await Promise.all([
        api.get("/auth/me"),
        api.get("/admin/settings"),
        api.get("/admin/vehicles"),
        api.get("/admin/inquiries"),
      ]);
      setAdmin(user.data);
      setSettings(currentSettings.data);
      setVehicles(currentVehicles.data);
      setInquiries(currentInquiries.data);
    } catch {
      navigate("/admin");
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async (data) => {
    setSaving(true);
    try {
      const response = await api.put("/admin/settings", data);
      setSettings(response.data);
      window.localStorage.setItem("urban-motors-content-updated", String(Date.now()));
      setNotice("Innholdet er lagret.");
    } catch (error) {
      setNotice(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const saveVehicle = async (data) => {
    setSaving(true);
    try {
      if (editing?.id) await api.put(`/admin/vehicles/${editing.id}`, data);
      else await api.post("/admin/vehicles", data);
      setEditing(null);
      setNotice("Bilen er lagret.");
      await load();
    } catch (error) {
      setNotice(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const deleteVehicle = async (id) => {
    if (!window.confirm("Slette denne bilen?")) return;
    await api.delete(`/admin/vehicles/${id}`);
    setNotice("Bilen er slettet.");
    await load();
  };

  const logout = async () => {
    await api.post("/auth/logout");
    navigate("/admin");
  };

  const syncFinn = async () => {
    setSyncingFinn(true);
    try {
      const response = await api.post("/admin/vehicles/sync-finn");
      const { imported, total, unpublished, updated } = response.data;
      setNotice(`FINN-synkronisering ferdig: ${total} aktive annonser, ${imported} nye, ${updated} oppdatert, ${unpublished} avpublisert.`);
      await load();
    } catch (error) {
      setNotice(formatApiError(error));
    } finally {
      setSyncingFinn(false);
    }
  };

  if (!admin || !settings) return <div className="site-loading" data-testid="admin-loading">Urban Motors</div>;

  return (
    <main className="admin-shell" data-testid="admin-page">
      <NoIndex />
      <aside className="admin-sidebar">
        <a href="/" data-testid="admin-home-link"><BrandLogo /></a>
        <div className="admin-profile" data-testid="admin-profile"><span>{admin.name.slice(0, 1)}</span><div><strong>{admin.name}</strong><small>{admin.email}</small></div></div>
        <nav data-testid="admin-navigation">
          <button className={tab === "overview" ? "active" : ""} data-testid="admin-overview-tab" onClick={() => { setTab("overview"); setEditing(null); }}><LayoutDashboard size={17} />Oversikt</button>
          <button className={tab === "vehicles" ? "active" : ""} data-testid="admin-vehicles-tab" onClick={() => { setTab("vehicles"); setEditing(null); }}><CarFront size={17} />Biler</button>
          <button className={tab === "settings" ? "active" : ""} data-testid="admin-settings-tab" onClick={() => { setTab("settings"); setEditing(null); }}><Settings2 size={17} />Innhold</button>
          <button className={tab === "copy" ? "active" : ""} data-testid="admin-copy-tab" onClick={() => { setTab("copy"); setEditing(null); }}><FileText size={17} />Tekster</button>
          <button className={tab === "inquiries" ? "active" : ""} data-testid="admin-inquiries-tab" onClick={() => { setTab("inquiries"); setEditing(null); }}><FileText size={17} />Henvendelser <em>{inquiries.length}</em></button>
        </nav>
        <button className="logout-button" data-testid="admin-logout-button" onClick={logout}><LogOut size={16} />Logg ut</button>
      </aside>
      <section className="admin-content">
        {notice && <p className="admin-notice" data-testid="admin-notice">{notice}</p>}
        {tab === "overview" && <div data-testid="admin-overview"><p className="section-eyebrow">Kontrollrom</p><h1>God dag, {admin.name.split(" ")[0]}.</h1><div className="stat-grid"><div data-testid="admin-vehicle-count"><span>Biler i sortimentet</span><strong>{vehicles.length}</strong></div><div data-testid="admin-inquiry-count"><span>Innkomne henvendelser</span><strong>{inquiries.length}</strong></div><div data-testid="admin-status"><span>Datakilde</span><strong>{vehicles.some((vehicle) => vehicle.source === "finn") ? "FINN + manuell" : "Manuell"}</strong></div></div><div className="overview-panel"><h2>Siste henvendelser</h2>{inquiries.length ? inquiries.slice(0, 3).map((inquiry) => <div className="inquiry-row" key={inquiry.id}><strong>{inquiry.name}</strong><span>{inquiry.registration_number}</span><small>{new Date(inquiry.created_at).toLocaleDateString("nb-NO")}</small></div>) : <p>Ingen henvendelser ennå.</p>}</div></div>}
        {tab === "settings" && <AdminSettingsPanel saving={saving} settings={settings} onSave={saveSettings} />}
        {tab === "copy" && <AdminTextEditor saving={saving} settings={settings} onSave={saveSettings} />}
        {tab === "vehicles" && <div data-testid="admin-vehicles-panel">{editing === null ? <><div className="admin-section-title"><div><p className="section-eyebrow">Sortiment</p><h1>Biler</h1></div><div className="admin-vehicle-actions"><button className="button admin-sync-button" data-testid="sync-finn-button" disabled={syncingFinn} onClick={syncFinn}><RefreshCw size={17} className={syncingFinn ? "spin" : ""} />{syncingFinn ? "Synkroniserer..." : "Synkroniser FINN"}</button><button className="button button--light" data-testid="add-vehicle-button" onClick={() => setEditing({})}><Plus size={17} />Ny bil</button></div></div><div className="admin-vehicles-list">{vehicles.map((vehicle) => <div className="admin-vehicle-row" data-testid={`admin-vehicle-${vehicle.id}`} key={vehicle.id}><img src={vehicle.main_image} alt="" /><div><strong>{vehicle.brand} {vehicle.model}</strong><span>{vehicle.variant} · {vehicle.year}{vehicle.source === "finn" ? " · FINN" : ""}</span></div><button className="table-action" data-testid={`edit-vehicle-${vehicle.id}`} onClick={() => setEditing(vehicle)}>Rediger</button><button aria-label="Slett bil" className="table-delete" data-testid={`delete-vehicle-${vehicle.id}`} onClick={() => deleteVehicle(vehicle.id)}><Trash2 size={16} /></button></div>)}</div></> : <AdminVehicleEditor saving={saving} vehicle={editing.id ? editing : null} onCancel={() => setEditing(null)} onSave={saveVehicle} />}</div>}
        {tab === "inquiries" && <div data-testid="admin-inquiries-panel"><p className="section-eyebrow">Kontakt</p><h1>Henvendelser</h1><div className="inquiry-list">{inquiries.length ? inquiries.map((inquiry) => <article className="inquiry-card" key={inquiry.id} data-testid={`inquiry-${inquiry.id}`}><div><strong>{inquiry.name}</strong><span>{inquiry.registration_number}</span></div><a href={`mailto:${inquiry.email}`} data-testid={`inquiry-email-${inquiry.id}`}>{inquiry.email}</a><a href={`tel:${inquiry.phone}`} data-testid={`inquiry-phone-${inquiry.id}`}>{inquiry.phone}</a><p>{inquiry.message}</p><small>{new Date(inquiry.created_at).toLocaleString("nb-NO")} · {inquiry.delivery_status === "sent" ? "Sendt" : "Venter på e-postoppsett"}</small></article>) : <p>Ingen henvendelser ennå.</p>}</div></div>}
      </section>
    </main>
  );
}